import styled from '@emotion/styled';
import {
    BodyTextMedium,
    BodyTextSmall,
    Button,
    Grid,
    GridItem,
    HeadingText30,
    HeadingText40,
    HeadingText60,
    pxToRem,
} from '@modernhealth/vital-components';
import { colors, layout } from '@modernhealth/vital-foundation';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import AboutModernPathways from '../assets/about-modern-pathways.png';

import { PathwayCloudBackground } from '../components/PathwayCloudBackground';
import { PathwaysCommonQuestions } from '../components/PathwaysCommonQuestions';

export const PathwaysAboutPage = () => {
    const { t } = useTranslation();
    const history = useHistory();

    return (
        <Container>
            <PathwayCloudBackground />
            <BackButton
                variant="flat"
                size="medium"
                icon="arrow-left"
                onClick={() => {
                    history.goBack();
                }}
            />
            <BannerContainer>
                <BannerContentContainer>
                    <HeadingText60 fontColor="white" marginBottom={8}>
                        {t('pathways:about.banner.title')}
                    </HeadingText60>
                    <BodyTextSmall fontColor="white">
                        {t('pathways:about.banner.description')}
                    </BodyTextSmall>
                </BannerContentContainer>

                <BannerImage src={AboutModernPathways} />
            </BannerContainer>
            <HeadingText40 marginBottom={24}>
                {t('pathways:about.how_it_works.header')}
            </HeadingText40>
            <Grid lgMode="3columns" css={{ marginBottom: pxToRem(60) }}>
                {steps.map((step, index) => {
                    const cardNumber = index + 1;
                    const { title, description } = step;

                    return (
                        <GridItem key={title} lg={1} md={2} column>
                            <Card
                                cardNumber={cardNumber}
                                title={t(title)}
                                description={t(description)}
                            />
                        </GridItem>
                    );
                })}
            </Grid>

            <HeadingText40 css={{ paddingBottom: pxToRem(16) }}>
                {t('pathways:about.common_questions.header')}
            </HeadingText40>
            {questions.map(({ question, answer }) => {
                return (
                    <PathwaysCommonQuestions key={question} question={question} answer={answer} />
                );
            })}
        </Container>
    );
};
const Card: React.FC<{ cardNumber: number; title: string; description: string }> = ({
    cardNumber,
    title,
    description,
}) => {
    return (
        <CardContent>
            <Step>
                <BodyTextMedium bold>{cardNumber}</BodyTextMedium>
            </Step>
            <HeadingText30>{title}</HeadingText30>
            <BodyTextMedium>{description}</BodyTextMedium>
        </CardContent>
    );
};

const BANNER_HEIGHT = 220;

const Container = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'center',
    width: '100%',
    maxWidth: layout.containerWidth,
    padding: pxToRem(16),
    marginBottom: pxToRem(60),
});
const BannerContainer = styled.div({
    display: 'flex',
    flex: 1,
    height: pxToRem(BANNER_HEIGHT),
    background: 'linear-gradient(108.02deg, #006BC6 25.12%, #00529c 100%);',
    alignItems: 'center',
    justifyContent: 'space-between',
    overflowX: 'clip',
    maxHeight: pxToRem(BANNER_HEIGHT),
    borderRadius: pxToRem(8),
    marginBottom: pxToRem(60),
});
const BannerContentContainer = styled.div({
    minWidth: '40%',
    padding: pxToRem(32),
});
const BannerImage = styled.img({
    height: pxToRem(BANNER_HEIGHT),
    objectFit: 'cover',
    padding: `0 ${pxToRem(24)}`,
});
const CardContent = styled.div({
    border: `${pxToRem(2)} solid ${colors.gray05}`,
    borderRadius: pxToRem(8),
    padding: pxToRem(24),
    minWidth: pxToRem(350),
});
const Step = styled.div({
    display: 'flex',
    width: pxToRem(36),
    height: pxToRem(36),
    marginBottom: pxToRem(16),
    border: `${pxToRem(2)} solid ${colors.blue55}`,
    borderRadius: pxToRem(50),
    color: `${colors.blue55}`,
    justifyContent: 'center',
    alignItems: 'center',
});
const BackButton = styled(Button)({
    marginBottom: pxToRem(20),
    paddingLeft: 0,
    justifyContent: 'flex-start',
    width: 'min-content',
});

const steps = [
    {
        title: 'pathways:about.how_it_works.0.title',
        description: 'pathways:about.how_it_works.0.description',
    },
    {
        title: 'pathways:about.how_it_works.1.title',
        description: 'pathways:about.how_it_works.1.description',
    },
    {
        title: 'pathways:about.how_it_works.2.title',
        description: 'pathways:about.how_it_works.2.description',
    },
];

const questions = [
    {
        question: 'pathways:about.common_questions.0.question',
        answer: 'pathways:about.common_questions.0.answer',
    },
    {
        question: 'pathways:about.common_questions.1.question',
        answer: 'pathways:about.common_questions.1.answer',
    },
    {
        question: 'pathways:about.common_questions.2.question',
        answer: 'pathways:about.common_questions.2.answer',
    },
    {
        question: 'pathways:about.common_questions.3.question',
        answer: 'pathways:about.common_questions.3.answer',
    },
    {
        question: 'pathways:about.common_questions.4.question',
        answer: 'pathways:about.common_questions.4.answer',
    },
];
